import React, { useEffect, useState } from "react";
import { Eye, Tag, QrCode, Info, Map, Navigation } from "lucide-react";
import api from "../../api";
import { useLang } from "../../context/LanguageContext";

const STEPS = [
  { key: "views", icon: Eye, color: "#2563eb", tint: "#e6effd" },
  { key: "grabs", icon: Tag, color: "#16a34a", tint: "#e7f6ec" },
  { key: "redeemed", icon: QrCode, color: "#b8860b", tint: "#fdf6e3" },
];

export const HonestFunnel = ({ funnel, directions }) => {
  const { t } = useLang();
  const f = funnel || { views: 0, grabs: 0, redeemed: 0, grabRate: 0, redeemRate: 0 };
  const max = Math.max(1, f.views);
  const rate = { views: null, grabs: t("fn.grabRate", { n: f.grabRate }), redeemed: t("fn.redeemRate", { n: f.redeemRate }) };

  return (
    <div className="bg-card border border-border rounded-2xl p-5" data-testid="honest-funnel">
      <h3 className="font-bold text-foreground mb-1">{t("fn.title")}</h3>
      <p className="text-xs text-muted-foreground mb-5">{t("fn.subtitle")}</p>
      <div className="space-y-4">
        {STEPS.map(({ key, icon: Icon, color, tint }) => (
          <div key={key} data-testid={`funnel-step-${key}`}>
            <div className="flex items-center justify-between gap-3 mb-1.5 flex-wrap">
              <span className="flex items-center gap-2 text-sm font-semibold text-foreground">
                <span className="h-7 w-7 rounded-lg flex items-center justify-center shrink-0" style={{ background: tint }}>
                  <Icon size={15} style={{ color }} />
                </span>
                {t(`fn.${key}`)}
              </span>
              <span className="flex items-baseline gap-2">
                <b className="text-lg font-extrabold text-foreground" data-testid={`funnel-count-${key}`}>{(f[key] || 0).toLocaleString()}</b>
                {rate[key] && <span className="text-xs text-muted-foreground" data-testid={`funnel-rate-${key}`}>{rate[key]}</span>}
              </span>
            </div>
            <div className="h-3 rounded-full bg-secondary overflow-hidden">
              <div className="h-full rounded-full transition-all duration-500" style={{ width: `${Math.max(f[key] ? 4 : 0, ((f[key] || 0) / max) * 100)}%`, background: color }} />
            </div>
            <p className="text-[11px] text-muted-foreground mt-1.5">{t(`fn.${key}Note`)}</p>
          </div>
        ))}
      </div>
      <div className="mt-5 rounded-xl border border-[#2563eb]/25 bg-[#e6effd]/60 dark:bg-[#12233f]/60 p-3 flex items-start gap-2" data-testid="funnel-directions">
        <Navigation size={15} className="text-[#2563eb] mt-0.5 shrink-0" />
        <div>
          <p className="text-xs font-semibold text-foreground">{t("fn.directionsTitle", { n: (directions || 0).toLocaleString() })}</p>
          <p className="text-xs text-muted-foreground mt-0.5">{t("fn.directionsNote")}</p>
        </div>
      </div>
      <div className="mt-3 rounded-xl bg-secondary/60 border border-border p-3 flex items-start gap-2" data-testid="funnel-honesty-note">        <Info size={15} className="text-muted-foreground mt-0.5 shrink-0" />
        <div>
          <p className="text-xs font-semibold text-foreground">{t("fn.honestTitle")}</p>
          <p className="text-xs text-muted-foreground mt-0.5">{t("fn.honestBody")}</p>
        </div>
      </div>
    </div>
  );
};

export const TrafficSources = () => {
  const { t } = useLang();
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/my/traffic-sources").then((r) => setData(r.data)).catch(() => setData(null)); }, []);

  const rows = data?.sources || [];
  return (
    <div className="bg-card border border-border rounded-2xl p-5" data-testid="traffic-sources">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <h3 className="font-bold text-foreground flex items-center gap-2"><Map size={18} className="text-[#16a34a]" /> {t("fn.sourcesTitle")}</h3>
        <span className="text-xs text-muted-foreground">{t("fn.last30Days")}</span>
      </div>
      {rows.length === 0 ? (
        <p className="text-sm text-muted-foreground py-6 text-center" data-testid="traffic-sources-empty">{t("fn.sourcesEmpty")}</p>
      ) : (
        <div className="space-y-3">
          {rows.map((s) => (
            <div key={s.source} data-testid={`traffic-source-${s.source}`}>
              <div className="flex justify-between text-sm mb-1">
                <span className="font-medium text-foreground">{s.label}</span>
                <span className="text-muted-foreground">{t("fn.nViewsPercent", { n: s.views, p: s.share })}</span>
              </div>
              <div className="h-2.5 rounded-full bg-secondary overflow-hidden">
                <div className="h-full rounded-full bg-[#16a34a] transition-all duration-500" style={{ width: `${Math.max(4, s.share)}%` }} />
              </div>
            </div>
          ))}
          <p className="text-[11px] text-muted-foreground pt-1">{t("fn.sourcesNote")}</p>
        </div>
      )}
    </div>
  );
};
